/* eslint-disable no-restricted-syntax */
/**
 * Store constants related to marketing instrumentation events
 */

export const EVENTS = {
  QUERIED_MAP: 'queried_map',
  QUERY_CARD_SECTION_VIEWED: 'query_card_section_viewed',
  QUERY_CARD_SECTION_OPENED: 'query_card_section_opened',
  QUERY_CARD_DISMISSED: 'query_card_dismissed',
  LAYER_DETAILS: 'layer_details_opened',
  FILTER_CHANGED: 'map_filter_changed',
  FILTER_RESET: 'filter_card_reset',
  MAP_LEGEND: 'map_legend_viewed',
  FILTER_CARD_OPENED: 'filter_card_opened',
  SEARCH: 'search_performed',
  SEARCH_FILTER_RESET: 'search_filter_reset',
  SEARCH_RESULT_SELECTED: 'selected_search_result',
  SEARCH_INITIATED: 'search_initiated',
  DISCOVER_LIST_VIEWED: 'discover_card_list_viewed',
  DISCOVER_CARD_SEE_ALL: 'discover_card_see_all',
  EXTERNAL_LINK_CLICKED: 'external_link_clicked',
  PHOTO_VIEWER_OPENED: 'photo_viewer_opened',
  PHOTO_VIEWER_CLOSED: 'photo_viewer_closed',
  PHOTO_VIEWED: 'photo_viewed',
  COORDINATES_COPIED: 'coordinates_copied',
  FEEDBACK_INITIATED: 'map_feedback_initiated',
  SUGGEST_EDIT_INITIATED: 'suggest_edit_initiated',
  SUGGEST_EDIT_SUBMITTED: 'suggest_edit_submitted',
  SUGGEST_EDIT_DISCARDED: 'suggest_edit_discarded',
}

// Used for the 'origin' property of queried_map events
export const CARD_ORIGINS = Object.freeze({
  SEARCH: 'search result',
  DISCOVER: 'discover card',
  DISCOVER_NEARBY: 'discover card nearby routes',
  DISCOVER_CLASSICS: 'discover card regional classics',
  DISCOVER_LOW_ANGLE: 'discover card low angle descent',
  DISCOVER_SHORTER: 'discover card shorter routes',
  DISCOVER_ADVENTUROUS: 'discover card adventurous routes',
  DISCOVER_RECENTLY_VIEWED: 'discover card recently viewed routes',
  RECENTLY_VIEWED: 'discover card see all recently viewed routes',
  ROUTE_GROUP_CLASSICS: 'discover card see all regional classics',
  ROUTE_GROUP_LOW_ANGLE: 'discover card see all low angle descent',
  ROUTE_GROUP_SHORTER: 'discover card see all shorter routes',
  DISCOVER_ABUNDANT_WALLEYE: 'discover card abundant walleye',
  DISCOVER_ABUNDANT_PANFISH: 'discover card abundant panfish',
  DISCOVER_TROPHY_WALLEYE: 'discover card trophy walleye',
  WATERBODY_GROUP_ABUNDANT_WALLEYE: 'discover card see all abundant walleye',
  WATERBODY_GROUP_ABUNDANT_PANFISH: 'discover card see all abundant panfish',
  WATERBODY_GROUP_TROPHY_WALLEYE: 'discover card see all trophy walleye',
  ROUTE_GROUP_ADVENTUROUS: 'discover card see all adventurous routes',
  AREA: 'area label',
  MAP_TAP: 'map tap',
  NEARBY_TRAILS: 'nearby trails',
  SNOTEL: 'snotel',
  DIRECT: 'direct link',
  ZONE: 'zone card',
  ZONE_CARD_ROUTES: 'zone card snowroutes',
  SHARE_PREVIEW: 'share preview',
  SHARE_TOKEN_REFRESH: 'share auth token refresh',
  SNOW_ROUTE: 'snowroute card',
  SNOW_ROUTE_NEARBY: 'snowroute card nearby snowroutes',
  REGION: 'region card',
  REGION_CARD_ZONES: 'region card zones',
  REGION_OVERVIEW: 'region overview tab',
  REGION_ZONES: 'region zones tab',
  ZONE_OVERVIEW: 'zone overview tab',
  ZONE_ROUTES: 'zone snowroutes tab',
  FILTER_ICON: 'map filter icon',
  LAYER_LIST: 'layer list',
  LAYER_DETAILS: 'layer details',
  MY_CONTENT_FAVORITES: 'my content favorite list',
  ADVENTURE: 'adventure card',
  BASEMAP: 'basemap card',
  WATER_ACCESS_OVERVIEW: 'water access card',
  QUERY_CARD: 'query card',
  QUERY_CARD_HEADER: 'query card header',
  QUERY_CARD_HEADER_MENU: 'query card header menu',
  QUERY_CARD_LIST: 'query card list',
  QUERY_CARD_NEARBY: 'query card nearby routes',
  MAP: 'map',
  TRAILHEAD_CARD: 'trailhead card',
  TRAILHEAD_CARD_ROUTES: 'trailhead card routes',
  AI_GUIDE_FIND_PLACES: 'ai guide find places tool',
  AI_GUIDE_QUERY_CARD: 'ai guide query card tool',
  AI_GUIDE_RESPONSE_LINK: 'ai guide response link',
})

export const QUERY_CARD_SECTIONS = Object.freeze({
  WEATHER: 'weather',
  REVIEWS: 'reviews',
  NEARBY: 'nearby routes',
  TRAIL_REPORT: 'trail reports',
  ATTRIBUTION: 'attribution',
  LOCAL_EXPERTS: 'local experts',
  SNOTEL: 'snotel',
  ZONES: 'zones',
})

// Used for the 'origin' property for Discover Card events
export const DISCOVER_ORIGINS = Object.freeze({
  MENU: 'global nav',
  REGION: 'region card',
  LAUNCH: 'app launch',
  FILTER: 'filter card',
  MODE: 'mode card',
  DIRECT: 'direct link',
  AREA: 'area label',
  WELCOME: 'welcome screen',
  SEARCH: 'search',
})

// Used for the 'origin' property for Discover Card See All event
export const DISCOVER_GROUP_ORIGINS = Object.freeze({
  CLASSIC: 'regional classics',
  SHORTER: 'shorter routes',
  ADVENTUROUS: 'adventurous routes',
  LOW_ANGLE: 'low angle descent',
})

// Used for the 'origin' property in activity filtering events
export const FILTER_ORIGINS = Object.freeze({
  DISCOVER: 'discover card',
  MODE_SELECTOR: 'mode selector',
  WELCOME: 'welcome screen',
  MAP: 'map',
})

// Used for the 'origin' properties in offline maps components events
export const OFFLINE_MAPS_ORIGINS = Object.freeze({
  DIRECT_LINK: 'direct link',
  IMAGERY_DETAILS: 'imagery details',
  MAP_EDIT_CARD: 'map edit card',
  MENU: 'menu',
  MAP_INFO_CARD: 'map info card',
  MAP_TOOLS: 'map tools',
  QUERY_CARD_SAVE_OFFLINE: 'query card save offline',
  WELCOME: 'welcome screen',
  NAV: 'global nav',
  QUERY_CARD_HEADER_MENU: 'query card header menu',
})

// Used for the 'origin' property on markup create card events
export const MARKUP_CREATE_ORIGINS = Object.freeze({
  WAYPOINT_TOOLS: 'tools add waypoint',
  WAYPOINT_QUERY_CARD: 'query card add waypoint',
  WAYPOINT_RICH_CONTENT: 'rich content card add waypoint',
  WAYPOINT_USER_STATS: 'user stats add waypoint',
  PHOTO_TOOLS: 'tools add photo',
  PHOTO_QUERY_CARD: 'query card add photo',
  PHOTO_RICH_CONTENT: 'rich content add photo',
  PHOTO_USER_STATS: 'user stats add photo',
  LINE_TOOLS: 'tools add line',
  AREA_TOOLS: 'tools add area',
  ROUTE_TOOLS: 'tools build route',
  ROUTE_QUERY_CARD: 'query card route to',
  ROUTE_RICH_CONTENT_CARD: 'rich content card route to',
  ROUTE_MARKUP_INFO_CARD: 'markup info card route to',
  MAP_LONG_PRESS: 'map long press',
  ONX_GUIDE: 'onx guide',
})

// Used for the 'origin' property on markup edit card events
export const MARKUP_EDIT_ORIGINS = Object.freeze({
  VIEW_CARD: 'markup info card',
  MY_CONTENT_EDIT: 'my content edit',
  MY_CONTENT_PHOTO: 'my content add photo',
  MY_CONTENT_TRACK_TRIM: 'my content trim',
  QUERY_CARD_EDIT: 'query card edit',
  QUERY_CARD_PHOTO: 'query card add photo',
  CLONE_SHARED_MARKUP: 'shared markup cloning',
})

// Used for the '[waypoint|line|shape|route]_info_card_opened' when viewing a markup
export const MARKUP_VIEW_ORIGINS = Object.freeze({
  MAP_VIEW: 'map view',
  MY_CONTENT: 'my content',
  COLLECTION_CARD: 'folder',
  QUERY_CARD: 'query card',
  IMPORT_SUCCESS_CARD: 'imported content card',
})

// Used for the 'origin' property when sharing items
export const SHARE_CREATE_ORIGINS = Object.freeze({
  VIEW_CARD: 'markup info card',
  MY_CONTENT_SELECT: 'my content select mode',
  MY_CONTENT_ITEM_MENU: 'my content markup menu',
  QUERY_CARD: 'query card',
  QUERY_CARD_MENU: 'query card markup menu',
  QUERY_CARD_SELECT: 'query card content select mode',
  CONTENT_COLLECTION_DETAILS_CARD: 'content collection info card',
  CONTENT_COLLECTION_ITEM_MENU: 'content collection markup menu',
  CONTENT_COLLECTION_ITEM_ELLIPSIS_MENU: 'my content group header menu',
  SHARE_CONTENT_SELECT: 'share content select mode',
  IMPORTED_CONTENT: 'imported content card',
  MANAGE_ACCESS_CARD: 'manage access card',
})

// Used for the 'origin' property when entering select mode
export const SELECT_MODE_ORIGINS = Object.freeze({
  MY_CONTENT_CARD: 'my content header',
  MARKUP_GROUP: 'my content group header',
  ALL_MARKUP_GROUP: 'my content all group',
  MARKUP_SHARE_BUTTON: 'my content markup menu share',
})

// Use for the 'origin' property on my garage events
export const MY_GARAGE_ORIGINS = Object.freeze({
  MENU: 'global nav',
  DIRECT: 'direct link',
  GARAGE: 'my garage',
  STATS: 'user stats',
})

export const FILTER_PROPERTIES = Object.freeze({
  CHANGE: 'filter_change',
  APPLIED: 'filters_applied',
  TYPE: 'filter_type',
  STATUS: 'filter_status',
})

export const LEGEND_ORIGINS = Object.freeze({
  MAP_MODES: 'mode card',
  HAMBURGER: 'hamburger menu',
  BASEMAP: 'basemap menu',
  LEGEND_TAB: 'legend tab',
})

export const BASEMAP_ORIGINS = Object.freeze({
  WELCOME: 'welcome screen',
})

export const SEARCH_ORIGINS = Object.freeze({
  SEARCH_BAR: 'search bar',
  SEARCH_AREA: 'search this area',
  SEARCH_EMPTY_STATE: 'search empty state',
  QUICK_SEARCH_RESULT_LIST: 'quick search result list',
  AUTOCOMPLETE_CURRENT_QUERY: 'autocomplete current query',
  AUTOCOMPLETE_SUGGESTED_QUERY: 'autocomplete suggested query',
  AUTOCOMPLETE_KEYBOARD: 'autocomplete keyboard',
})

export const SEARCH_RESULT_SELECTED_ORIGINS = Object.freeze({
  RESULT_LIST: 'search result list',
  RESULT_MAP_MARKER: 'search result pin',
  AUTOCOMPLETE_RESULT_LIST: 'autocomplete result list',
})

export const CONTENT_TYPE_ORIGINS = Object.freeze({
  MY_CONTENT: 'my content',
  MY_CONTENT_ICON: 'my content icon',
  ALL_CONTENT: 'all content list',
  WAYPOINT_LIST: 'waypoint list',
  TRACK_LIST: 'track list',
  LINE_LIST: 'line list',
  SHAPE_LIST: 'shape list',
  ROUTE_LIST: 'route list',
  WAYPOINT_VIEW: 'waypoint detail',
  TRACK_VIEW: 'track detail',
  LINE_VIEW: 'line detail',
  SHAPE_VIEW: 'shape detail',
  ROUTE_VIEW: 'route detail',
  LIST_SELECT: 'my content list select mode',
  MY_CONTENT_SELECT: 'my content select mode',
  FILTER_CARD: 'filter card',
  MARKUP_ADD_TO_FOLDER: 'markup info card folder button',
  DIRECT_LINK: 'direct link',
})

export const CONTENT_COLLECTION_ORIGINS = Object.freeze({
  ALL_COLLECTIONS_CARD: 'content collection list',
  ALL_COLLECTIONS_COLLECTION_ICON: 'content collection list icon',
  COLLECTION_SELECTOR: 'content collection selector',
  COLLECTION_ADD_CONTENT: 'content collection add content',
  COLLECTION_REMOVE_CONTENT: 'content collection remove content',
  COLLECTION_ITEM_ELLIPSIS_MENU: 'my content group header menu',
  QUERY_CARD: 'query card',
})

export const CONTENT_COLLECTION_EVENTS = Object.freeze({
  CREATE_STARTED: 'content_collection_create_initiated',
  CREATE_SAVED: 'content_collection_create_saved',
  EDIT_SAVED: 'content_collection_edit_saved',
  DELETED: 'content_collection_deleted',
  LEAVE_FOLDER: 'content_collection_share_removed',
  SELECTION_STARTED: 'content_collection_selection_initiated',
  SELECTION_DISCARDED: 'content_collection_selection_discarded',
  CONTENT_ADD_STARTED: 'content_collection_add_initiated',
  CONTENT_ADDED: 'content_collection_content_added',
  CONTENT_ADD_DISCARDED: 'content_collection_add_discarded',
  CONTENT_REMOVED: 'content_collection_content_removed',
  LIST_VIEWED: 'content_collection_list_viewed',
})

export const NETWORK_STATUSES = Object.freeze({
  OFFLINE: 'offline',
  ONLINE: 'online',
})

export const PURCHASE_CTA_ORIGINS = Object.freeze({
  ACCESS_3D_MAPS: 'access 3d maps',
  WIND_DIRECTION_CARD: 'wind direction card',
  ROUTE_BUILDER: 'route builder',
  TRIAL_DENIED: 'trial denied',
  ACCOUNT_CARD: 'account card',
  DISCOVER_CARD: 'discover',
  FILTERS_CARD: 'filters',
  QUERY_CARD: 'query card',
  QUERY_CARD_REVIEWS: 'query card reviews',
  QUERY_CARD_NEARBY: 'query card nearby routes',
  QUERY_CARD_TRAIL_REPORTS: 'query card trail reports',
  MAP_SCREEN: 'map screen',
  ELITE_MEMBER_PANEL: 'elite member panel',
  MAP_LAYERS_CARD: 'map layers card',
  LAYER_UPSELL: 'layer upsell',
  IMPORT_CARD: 'import card',
  BASEMAPS_MODAL: 'basemaps modal',
  OFFLINE_MAPS_CARD: 'offline maps card',
  BASIC_UPSELL_MODAL: 'basic upsell modal',
  RECENT_IMAGERY: 'recent imagery',
  ELITE_FEATURE_CARD_PREFIX: 'elite feature details card',
  SNOW_REGION: 'region card',
  SNOW_REGION_ZONES: 'region card zones',
  SNOW_ZONE: 'zone card',
  SNOW_ZONE_ROUTES: 'zone card snowroutes',
  SNOW_ROUTE: 'snowroute card',
  SNOW_ROUTE_NEARBY: 'snowroutes card nearby snowroutes',
  CONTENT_COLLECTION_SHARE: 'content collection share',
  WATERBODY_CARD: 'waterbody card',
  FILTER_CARD: 'filter card',
  QUICK_FILTER: 'quick filter',
  SPECIES_DETAILS: 'species details',
  BASEMAP_CARD: 'basemap card',
  WATER_ACCESS: 'water access details',
  FISHING_AREA: 'fishing area details',
  FISHING_REPORT: 'fishing report details',
  GMU_SUBSCRIPTION_CARD: 'query hrt upgrade',
  ONX_GUIDE: 'onx guide',
})

export const UPGRADE_MESSAGES = Object.freeze({
  BC_ROUTE_DETAILS:
    'Unlock onX Backcountry to see all <strong>route details.</strong>"',
  BC_NEARBY_TRAILS:
    'Unlock onX Backcountry to get access to <strong>nearby trails.</strong>',
  BC_TRAIL_INFO: 'Unlock onX Backcountry to see trail information.',
  BC_REGION_DETAILS:
    'Unlock onX Backcountry to see all <strong>region details.</strong>',
  BC_ZONE_DETAILS:
    'Unlock onX Backcountry to see all <strong>zone details.</strong>',
})

export const PHOTO_VIEWER_ORIGINS = Object.freeze({
  TRAIL_REPORT: 'trail report photo',
  QUERY_CARD: 'query card',
})

export const WEATHER_EVENTS = Object.freeze({
  WEATHER_OPENED: 'weather_forecast_card_opened',
  WEATHER_CLOSED: 'weather_forecast_card_closed',
  WEATHER_CARD_TAB_VIEWED: 'weather_forecast_card_tab_viewed',
  WEATHER_WEEKLY_DAYS_EXPANDED: 'weekly_forecast_days_expanded',
  ANIMAL_FORECAST_INFO_TAPPED: 'animal_forecast_info_tapped',
})

export const WEATHER_ORIGINS = Object.freeze({
  WEATHER_CARD: 'weather forecast card',
  WEATHER_FEATURE_QUERY: 'query info card',
  WEATHER_WAYPOINT: 'waypoint info card',
  WEATHER_OVERVIEW_TAB_BUTTON: 'overview tab button',
  WEATHER_TAB: 'tab',
})

export const WIND_EVENTS = Object.freeze({
  WIND_CALENDAR_VIEWED: 'waypoint_wind_calendar_viewed',
  WAYPOINT_WIND_OPENED: 'waypoint_wind_card_opened',
  WAYPOINT_WIND_EDIT_DISCARDED: 'waypoint_wind_edit_discarded',
  WAYPOINT_WIND_MODIFIED: 'waypoint_wind_modify',
})

// Used for the 'origin' property on content editor events
export const CONTENT_EDITOR_ORIGINS = Object.freeze({
  CONTENT_EDITOR: 'content editor',
})
